/* Camera test: media stays in browser memory; no upload or persistence API. */
(() => {
  'use strict';
  const LIMIT_BYTES = 64 * 1024 * 1024;
  const LIMIT_SECONDS = 120;
  const MAX_PHOTOS = 6;
  const MIME_TYPES = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4;codecs=avc1.42E01E', 'video/mp4'];
  const AV_MIME_TYPES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  const initialize = (root) => {
    if (root.dataset.cameraInitialized) return;
    root.dataset.cameraInitialized = 'true';
    const el = (name) => root.querySelector(`[data-camera-${name}]`);
    const ui = Object.fromEntries(['video', 'stage', 'empty', 'grid-overlay', 'state', 'orientation', 'start', 'stop', 'fullscreen', 'status', 'dimensions', 'framerate', 'brightness', 'device', 'resolution', 'mirror', 'grid', 'save-mirror', 'snapshot', 'record', 'pause', 'finish', 'timer', 'record-badge', 'record-support', 'photos', 'clip', 'clip-details', 'clip-download', 'playback', 'delete-clip', 'captures-empty', 'mode', 'microphone', 'microphone-field', 'permission-hint', 'record-description', 'audio-panel', 'audio-level', 'audio-reading', 'audio-status', 'resume-audio'].map((name) => [name, el(name)]));
    const supported = window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;
    const recordingSupported = typeof window.MediaRecorder === 'function';
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    // A link may select a mode; permission still requires the visitor's Start click.
    ui.mode.value = new URLSearchParams(window.location.search).get('mode') === 'audio-video' ? 'audio-video' : 'camera';
    let stream = null;
    let activeMode = null;
    let audioContext = null;
    let audioSource = null;
    let audioAnalyser = null;
    let audioSamples = null;
    let lastAudioSample = 0;
    let audioResumeError = '';
    let pending = false;
    let requestId = 0;
    let sampleFrame = 0;
    let lastSample = 0;
    let session = null;
    let clipURL = null;
    let photoNumber = 0;
    let capturePending = false;
    const photos = new Map();
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = 64;
    sampleCanvas.height = 36;
    let sampleContext;
    try { sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true }); } catch (_) { sampleContext = null; }

    const say = (message) => { ui.status.textContent = message; };
    const audioStatus = (message) => { if (ui['audio-status'].textContent !== message) ui['audio-status'].textContent = message; };
    const selectedAV = () => ui.mode.value === 'audio-video';
    const clipReady = (current) => `Your ${current.hasAudio ? 'video with microphone audio' : 'silent video'} is ready to review and download.`;
    const closeContext = (context) => {
      if (!context || context.state === 'closed') return;
      try { Promise.resolve(context.close()).catch(() => {}); } catch (_) { /* Already closing. */ }
    };
    const clearAudioReading = (message) => {
      ui['audio-level'].value = 0;
      ui['audio-level'].setAttribute('aria-valuetext', message);
      ui['audio-reading'].textContent = '— dBFS';
    };
    const syncAudio = () => {
      const live = !!stream && activeMode === 'audio-video';
      const track = live ? stream.getAudioTracks()[0] : null;
      const paused = !!audioContext && audioContext.state !== 'running';
      ui['resume-audio'].hidden = !live || !audioAnalyser || !paused;
      ui['resume-audio'].disabled = !live || !audioAnalyser || !paused || audioContext?.state === 'closed';
      if (!live) {
        clearAudioReading('Microphone off');
        audioStatus('Start camera + microphone to check your input level. The live preview stays muted to prevent feedback.');
      } else if (!audioAnalyser) {
        clearAudioReading('Microphone meter unavailable');
        audioStatus('The live microphone meter is unavailable in this browser. You can still record both devices and play the clip to check your sound.');
      } else if (audioContext?.state === 'closed') {
        clearAudioReading('Microphone meter unavailable');
        ui['resume-audio'].hidden = true;
        audioStatus('The microphone meter closed. Stop and restart the devices to restore it. You can still record a clip to check your sound.');
      } else if (paused) {
        clearAudioReading('Microphone meter paused');
        audioStatus(audioResumeError || 'The microphone meter is paused. Choose Resume microphone meter to restore readings. Your microphone track and any active recording may still be running.');
      } else if (track?.muted) {
        clearAudioReading('Microphone input interrupted');
        audioStatus('The browser temporarily stopped receiving microphone audio. Check the device and keep this page open; recording may contain silence during the interruption.');
      } else {
        audioResumeError = '';
        audioStatus('Microphone connected. Speak normally and watch the input level. The preview is muted; play a recorded clip to hear your voice.');
      }
    };
    const stopTracks = (media) => media?.getTracks().forEach((track) => track.stop());
    const timestamp = () => new Date().toISOString().replace(/[:.]/g, '-');
    const duration = (seconds) => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
    // Native interruptions can set recorder.state to inactive before stop is
    // delivered. Track time independently so that final active segment survives.
    const capturedSeconds = (current) => (current.elapsed + (current.running ? performance.now() - current.started : 0)) / 1000;
    const sync = () => {
      const on = !!stream;
      const busy = !!session;
      const combined = selectedAV();
      ui.mode.disabled = pending || busy;
      ui.microphone.disabled = !on || activeMode !== 'audio-video' || pending || busy;
      ui['microphone-field'].hidden = !combined;
      ui['audio-panel'].hidden = !combined;
      ui.start.textContent = combined ? 'Start camera + microphone' : 'Start camera';
      ui.stop.textContent = combined ? 'Stop camera + microphone' : 'Stop camera';
      ui['permission-hint'].textContent = combined ? 'Allow camera and microphone access when your browser asks. The live preview stays muted.' : 'Camera-only mode. Allow camera access when your browser asks.';
      ui['record-description'].textContent = combined ? 'Combined mode records video with microphone audio.' : 'Camera-only mode records silent video.';
      ui.start.disabled = !supported || pending || on || busy;
      ui.stop.disabled = !on && !pending;
      ui.device.disabled = !on || pending || busy;
      ui.resolution.disabled = pending || busy;
      ui.snapshot.disabled = !on || pending || capturePending || photos.size >= MAX_PHOTOS;
      ui.fullscreen.disabled = !on || !ui.stage.requestFullscreen;
      ui.record.disabled = !on || pending || busy || !recordingSupported;
      ui.pause.disabled = !session || session.finishing || !session.canPause;
      ui.finish.disabled = !session || session.finishing;
      ui.pause.textContent = session?.recorder.state === 'paused' ? 'Resume' : 'Pause';
      ui.state.textContent = pending ? 'Connecting…' : session && !session.finishing ? (session.recorder.state === 'paused' ? 'Recording paused' : 'Recording') : on ? (activeMode === 'audio-video' ? 'Camera + microphone on' : 'Camera on') : (combined ? 'Camera + microphone off' : 'Camera off');
      ui['record-badge'].hidden = !session || session.finishing;
      ui['record-badge'].textContent = session?.recorder.state === 'paused' ? 'Paused' : 'Recording';
      ui['captures-empty'].hidden = !!clipURL || photos.size > 0;
      syncAudio();
    };
    const updateOrientation = () => {
      ui.video.dataset.mirrored = String(ui.mirror.checked);
      ui.orientation.textContent = ui.mirror.checked ? 'Mirrored preview' : 'Original orientation';
      ui['grid-overlay'].hidden = !ui.grid.checked || !stream;
    };
    const showDimensions = () => {
      if (!stream) return;
      const settings = stream.getVideoTracks()[0]?.getSettings?.() || {};
      const width = ui.video.videoWidth || settings.width;
      const height = ui.video.videoHeight || settings.height;
      ui.dimensions.textContent = width && height ? `${width} × ${height}` : 'Not reported';
      ui.framerate.textContent = settings.frameRate ? `${Math.round(settings.frameRate * 10) / 10} fps` : 'Not reported';
      if (width && height) ui.stage.style.aspectRatio = `${width} / ${height}`;
    };
    const sampleBrightness = (now) => {
      if (!stream) return;
      if (activeMode === 'audio-video' && audioAnalyser && now - lastAudioSample >= 250) {
        lastAudioSample = now;
        if (!stream.getAudioTracks()[0] || stream.getAudioTracks()[0].readyState === 'ended') {
          stopCamera('Microphone disconnected or permission was revoked. Both devices stopped; reconnect the microphone and start again.');
          return;
        }
        // Suspended/interrupted contexts and muted tracks are not silence readings.
        if (audioContext?.state !== 'running' || stream.getAudioTracks()[0]?.muted) syncAudio();
        else {
          try {
            audioAnalyser.getFloatTimeDomainData(audioSamples);
            let sum = 0;
            for (let i = 0; i < audioSamples.length; i++) sum += audioSamples[i] * audioSamples[i];
            const rms = Math.sqrt(sum / audioSamples.length);
            const db = rms > .00001 ? 20 * Math.log10(rms) : -100;
            const text = db <= -100 ? 'Below −100 dBFS' : `${db.toFixed(1)} dBFS`;
            ui['audio-reading'].textContent = text;
            ui['audio-level'].value = Math.max(0, Math.min(100, (db + 60) / 60 * 100));
            ui['audio-level'].setAttribute('aria-valuetext', text);
          } catch (_) {
            audioAnalyser = null;
            syncAudio();
          }
        }
      }
      if (!document.hidden && sampleContext && ui.video.readyState >= 2 && now - lastSample > 1000) {
        lastSample = now;
        try {
          sampleContext.drawImage(ui.video, 0, 0, 64, 36);
          const pixels = sampleContext.getImageData(0, 0, 64, 36).data;
          let total = 0;
          for (let i = 0; i < pixels.length; i += 4) total += .2126 * pixels[i] + .7152 * pixels[i + 1] + .0722 * pixels[i + 2];
          ui.brightness.textContent = `${Math.round(total / (pixels.length / 4) / 255 * 100)} / 100`;
        } catch (_) {
          ui.brightness.textContent = 'Unavailable';
          sampleContext = null;
        }
      }
      sampleFrame = requestAnimationFrame(sampleBrightness);
    };
    const clearClip = () => {
      ui.playback.pause();
      ui.playback.removeAttribute('src');
      ui.playback.load();
      if (clipURL) URL.revokeObjectURL(clipURL);
      clipURL = null;
      ui['clip-download'].removeAttribute('href');
      ui.clip.hidden = true;
      sync();
    };
    const finishRecording = (message) => {
      if (!session || session.finishing) return;
      const current = session;
      current.seconds = capturedSeconds(current);
      current.running = false;
      ui.timer.textContent = duration(Math.min(current.seconds, LIMIT_SECONDS));
      current.finishing = true;
      current.message = message || clipReady(current);
      clearInterval(current.timer);
      try {
        if (current.recorder.state !== 'inactive') current.recorder.stop();
      } catch (_) {
        current.discard = true;
        session = null;
        say('The browser could not finish this recording. Try a new short clip.');
      }
      sync();
    };
    const stopCamera = (message, discard = false) => {
      requestId += 1;
      pending = false;
      capturePending = false;
      if (session) {
        session.discard ||= discard;
        session.message = message ? `${message} ${clipReady(session)}` : `Devices stopped. ${clipReady(session)}`;
        finishRecording(session.message);
      }
      const previous = stream;
      stream = null;
      activeMode = null;
      stopTracks(previous);
      const previousContext = audioContext;
      audioContext = null;
      if (previousContext) previousContext.onstatechange = null;
      [audioSource, audioAnalyser].forEach((node) => { try { node?.disconnect(); } catch (_) { /* Already disconnected. */ } });
      audioSource = audioAnalyser = audioSamples = null;
      audioResumeError = '';
      closeContext(previousContext);
      cancelAnimationFrame(sampleFrame);
      ui.video.pause();
      ui.video.srcObject = null;
      ui.video.hidden = true;
      ui.empty.hidden = false;
      ui['grid-overlay'].hidden = true;
      ui.dimensions.textContent = '—';
      ui.framerate.textContent = '—';
      ui.brightness.textContent = '—';
      if (message) say(message);
      sync();
    };
    const errorMessage = (error, combined) => {
      if (combined) {
        const messages = {
          NotAllowedError: 'Camera or microphone access was not allowed. Combined mode requires both permissions. Check this site’s camera and microphone permissions and your system privacy settings, then try again. You can also choose Camera only. Open an embedded preview in its own browser tab.',
          SecurityError: 'Camera or microphone access is blocked in this browser context. Open this secure page directly and check both device permissions, or choose Camera only.',
          NotFoundError: 'Both a camera and microphone are required for combined mode. Connect the missing device and try again, or choose Camera only.',
          NotReadableError: 'The camera or microphone could not start. Close apps using either device, check hardware privacy switches, and reconnect external devices. You can also try Camera only.',
          OverconstrainedError: 'The selected camera, microphone, or resolution is unavailable. Choose the default devices or a lower resolution, then start again.',
          AbortError: 'Camera + microphone startup was interrupted. Reconnect the devices if needed and try again.'
        };
        return messages[error?.name] || 'Camera + microphone could not start. Check both device permissions and connections, then try again or choose Camera only.';
      }
      const messages = {
        NotAllowedError: 'Camera access was not allowed. Check this site’s camera permission in your browser and your system privacy settings, then try again. In an embedded preview, open the page directly in your browser.',
        SecurityError: 'Camera access is disabled in this browser context. Open the secure page directly in your browser and check camera permissions.',
        NotFoundError: 'No available camera was found. Connect a webcam, open its privacy shutter, and try again.',
        NotReadableError: 'The camera could not start. Close other camera apps, check the privacy shutter or hardware switch, and reconnect an external webcam.',
        OverconstrainedError: 'That camera or resolution is unavailable. Choose the default camera or a lower resolution, then start again.',
        AbortError: 'Camera startup was interrupted. Reconnect the webcam if needed and try again.'
      };
      return messages[error?.name] || 'The camera could not start. Check browser and system permissions, close other camera apps, and try again.';
    };
    const listCameras = async (id) => {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (id !== requestId || !stream) return;
        const selected = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
        ui.device.replaceChildren(new Option('Default camera', ''));
        devices.filter((device) => device.kind === 'videoinput').forEach((device, i) => {
          ui.device.add(new Option(device.label || `Camera ${i + 1}`, device.deviceId));
        });
        if (selected && [...ui.device.options].some((option) => option.value === selected)) ui.device.value = selected;
        if (activeMode === 'audio-video') {
          const selectedMic = stream.getAudioTracks()[0]?.getSettings?.().deviceId;
          ui.microphone.replaceChildren(new Option('Default microphone', ''));
          devices.filter((device) => device.kind === 'audioinput' && device.deviceId !== 'default').forEach((device, i) => {
            ui.microphone.add(new Option(device.label || `Microphone ${i + 1}`, device.deviceId));
          });
          if (selectedMic && [...ui.microphone.options].some((option) => option.value === selectedMic)) ui.microphone.value = selectedMic;
        }
      } catch (_) { /* A working camera does not depend on enumerating other devices. */ }
    };
    const startCamera = async () => {
      if (!supported || pending || session) return;
      stopCamera();
      const id = ++requestId;
      const combined = selectedAV();
      pending = true;
      say(combined ? 'Waiting for camera and microphone permission. Combined mode needs both. Choose Allow in your browser, or Stop camera + microphone to cancel this request.' : 'Waiting for camera permission. Choose Allow in your browser, or Stop camera to cancel this request.');
      sync();
      const [width, height] = ui.resolution.value.split('x').map(Number);
      const constraints = { audio: false, video: { width: { ideal: width, max: 1920 }, height: { ideal: height, max: 1080 }, frameRate: { ideal: 30, max: 60 } } };
      if (combined) constraints.audio = ui.microphone.value ? { deviceId: { exact: ui.microphone.value } } : true;
      if (ui.device.value) constraints.video.deviceId = { exact: ui.device.value };
      // Resume from this explicit gesture. Analyzer failures must not prevent
      // recording the original combined stream or trigger a silent fallback.
      let localContext = null;
      if (combined && AudioContextClass) {
        try {
          localContext = new AudioContextClass();
          audioContext = localContext;
          if (localContext.state !== 'running') Promise.resolve(localContext.resume()).catch(() => {});
        } catch (_) { closeContext(localContext); localContext = audioContext = null; }
      }
      let opened;
      try {
        opened = await navigator.mediaDevices.getUserMedia(constraints);
        if (id !== requestId) { stopTracks(opened); closeContext(localContext); return; }
        stream = opened;
        activeMode = combined ? 'audio-video' : 'camera';
        const track = stream.getVideoTracks()[0];
        if (!track || track.readyState === 'ended') throw new Error('Camera stream ended during startup');
        const audioTrack = stream.getAudioTracks?.()[0];
        if (combined && (!audioTrack || audioTrack.readyState === 'ended')) throw Object.assign(new Error('A live microphone is required'), { name: 'NotFoundError' });
        track.addEventListener('ended', () => {
          if (stream === opened) stopCamera(combined ? 'Camera disconnected or permission was revoked. Both devices stopped. Reconnect the camera and start again.' : 'Camera disconnected or permission was revoked. Reconnect it and choose Start camera.');
        }, { once: true });
        if (combined) {
          audioTrack.addEventListener('ended', () => {
            if (stream === opened) stopCamera('Microphone disconnected or permission was revoked. Both devices stopped; reconnect the microphone and start again.');
          }, { once: true });
          ['mute', 'unmute'].forEach((event) => audioTrack.addEventListener(event, () => { if (stream === opened) syncAudio(); }));
          if (localContext && localContext.state !== 'closed') {
            try {
              audioSource = localContext.createMediaStreamSource(opened);
              audioAnalyser = localContext.createAnalyser();
              audioAnalyser.fftSize = 2048;
              audioSamples = new Float32Array(audioAnalyser.fftSize);
              audioSource.connect(audioAnalyser);
              // Deliberately no connection to the speaker destination.
              localContext.onstatechange = () => { if (audioContext === localContext && stream === opened) syncAudio(); };
            } catch (_) {
              [audioSource, audioAnalyser].forEach((node) => { try { node?.disconnect(); } catch (_) {} });
              audioSource = audioAnalyser = audioSamples = null;
              closeContext(localContext);
              audioContext = null;
            }
          }
        }
        ui.video.muted = true;
        ui.video.srcObject = stream;
        await ui.video.play();
        if (id !== requestId) { stopTracks(opened); closeContext(localContext); return; }
        pending = false;
        ui.video.hidden = false;
        ui.empty.hidden = true;
        showDimensions();
        updateOrientation();
        sampleFrame = requestAnimationFrame(sampleBrightness);
        say(combined ? 'Camera and microphone are on. Speak normally, check your input level, and record a clip with sound. The live preview stays muted.' : 'Camera is on. Check your framing, capture a photo, or record a silent test clip.');
        sync();
        listCameras(id);
      } catch (error) {
        if (id !== requestId) { stopTracks(opened); closeContext(localContext); return; }
        stopCamera(errorMessage(error, combined));
        if (error?.name === 'OverconstrainedError' || error?.name === 'NotFoundError') { ui.device.value = ''; ui.microphone.value = ''; }
      }
    };
    const snapshot = () => {
      if (!stream || capturePending || photos.size >= MAX_PHOTOS || ui.video.readyState < 2) {
        if (stream && ui.video.readyState < 2) say('The camera is starting. Wait for the preview, then capture your photo.');
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = ui.video.videoWidth;
      canvas.height = ui.video.videoHeight;
      const id = requestId;
      const mirrored = ui['save-mirror'].checked;
      capturePending = true;
      sync();
      try {
        const context = canvas.getContext('2d');
        if (!context || !canvas.width || !canvas.height) throw new Error('Canvas unavailable');
        if (mirrored) { context.translate(canvas.width, 0); context.scale(-1, 1); }
        context.drawImage(ui.video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (id !== requestId) return;
          capturePending = false;
          if (!blob || blob.size > 16 * 1024 * 1024) { say('This photo could not be saved. Try a lower camera resolution.'); sync(); return; }
          const number = ++photoNumber;
          const url = URL.createObjectURL(blob);
          const card = document.createElement('article');
          card.className = 'mm-tools__card';
          const img = document.createElement('img');
          img.src = url;
          img.alt = `Camera test photo ${number}${mirrored ? ', mirrored' : ', original orientation'}`;
          img.width = canvas.width;
          img.height = canvas.height;
          const heading = document.createElement('h3');
          heading.textContent = `Photo ${number}`;
          const info = document.createElement('p');
          info.textContent = `${canvas.width} × ${canvas.height} · ${mirrored ? 'Mirrored' : 'Original orientation'} · PNG`;
          const actions = document.createElement('div');
          actions.className = 'mm-tools__actions';
          const download = document.createElement('a');
          download.className = 'mm-tools__button';
          download.href = url;
          download.download = `muteme-camera-${timestamp()}-${number}.png`;
          download.textContent = 'Download PNG';
          download.setAttribute('aria-label', `Download photo ${number} as PNG`);
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'mm-tools__button mm-tools__button--secondary';
          remove.textContent = 'Delete';
          remove.setAttribute('aria-label', `Delete photo ${number}`);
          remove.addEventListener('click', () => {
            URL.revokeObjectURL(url);
            photos.delete(url);
            card.remove();
            say(`Photo ${number} deleted from this tab.`);
            sync();
            (ui.snapshot.disabled ? ui.start : ui.snapshot).focus();
          });
          actions.append(download, remove);
          card.append(img, heading, info, actions);
          ui.photos.append(card);
          photos.set(url, card);
          say(photos.size >= MAX_PHOTOS ? 'Photo captured. You have 6 photos; download and delete one before taking another.' : `Photo ${number} captured. Review and download it under Your session captures.`);
          sync();
        }, 'image/png');
      } catch (_) { capturePending = false; say('Your browser could not capture this photo. Try again after the preview starts.'); sync(); }
    };
    const startRecording = () => {
      if (!stream || session || !recordingSupported) return;
      const hasAudio = activeMode === 'audio-video';
      const microphone = stream.getAudioTracks?.()[0];
      if (hasAudio && (!microphone || microphone.readyState === 'ended')) {
        stopCamera('The microphone is no longer available. Combined recording requires a live microphone. Reconnect it or choose Camera only.');
        return;
      }
      ui.playback.pause();
      let recorder;
      try {
        const mime = (hasAudio ? AV_MIME_TYPES : MIME_TYPES).find((type) => MediaRecorder.isTypeSupported?.(type));
        recorder = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 4000000, ...(hasAudio ? { audioBitsPerSecond: 128000 } : {}) });
      } catch (_) { say('Video recording is not available for this camera in your browser. You can still preview and capture photos.'); return; }
      const current = { recorder, hasAudio, microphone: microphone?.label || 'Selected microphone', chunks: [], bytes: 0, elapsed: 0, started: performance.now(), wallStarted: performance.now(), seconds: 0, running: true, discard: false, finishing: false, canPause: typeof recorder.pause === 'function' && typeof recorder.resume === 'function' };
      session = current;
      recorder.addEventListener('dataavailable', (event) => {
        if (!event.data?.size || current.discard) return;
        if (current.bytes + event.data.size > LIMIT_BYTES) {
          current.discard = true;
          current.failure = 'This recording exceeded the 64 MB memory limit and was discarded. Try a shorter clip or lower resolution.';
          current.chunks = [];
          finishRecording();
          return;
        }
        current.chunks.push(event.data);
        current.bytes += event.data.size;
        if (current.bytes >= LIMIT_BYTES - 4 * 1024 * 1024) finishRecording(`The recording reached its size limit. ${clipReady(current)}`);
      });
      recorder.addEventListener('error', () => {
        current.failure = 'The browser could not complete this video. Try a shorter recording or another resolution.';
        current.discard = true;
        finishRecording();
      });
      recorder.addEventListener('stop', () => {
        clearInterval(current.timer);
        if (session !== current) return;
        if (!current.finishing) current.seconds = capturedSeconds(current);
        current.running = false;
        session = null;
        ui.timer.textContent = duration(current.seconds);
        if (!current.discard && current.chunks.length) {
          const mime = recorder.mimeType || current.chunks.find((chunk) => chunk.type)?.type || '';
          const blob = new Blob(current.chunks, { type: mime });
          const extension = mime.includes('mp4') ? 'mp4' : mime.includes('webm') ? 'webm' : mime.includes('ogg') ? 'ogv' : 'video';
          clearClip();
          clipURL = URL.createObjectURL(blob);
          ui.playback.src = clipURL;
          ui.playback.muted = false;
          ui.playback.setAttribute('aria-label', current.hasAudio ? 'Recorded camera test with microphone audio' : 'Recorded silent camera test');
          ui['clip-download'].href = clipURL;
          ui['clip-download'].download = `muteme-camera-${timestamp()}.${extension}`;
          ui['clip-download'].textContent = `Download ${extension === 'video' ? 'video' : extension.toUpperCase()}`;
          ui['clip-details'].textContent = `${duration(current.seconds)} · ${(blob.size / 1024 / 1024).toFixed(1)} MB · ${current.hasAudio ? `Microphone audio included (${current.microphone})` : 'Silent video'} · Original camera orientation`;
          ui.clip.hidden = false;
          say(current.message || clipReady(current));
        } else if (current.failure) say(current.failure);
        else if (!current.discard) say('The recording was too short to save. Try recording for a few seconds.');
        current.chunks = [];
        sync();
      });
      try {
        recorder.start(1000);
        current.started = performance.now();
        current.timer = setInterval(() => {
          if (session !== current || current.finishing) return;
          const seconds = capturedSeconds(current);
          ui.timer.textContent = duration(Math.min(seconds, LIMIT_SECONDS));
          if (seconds >= LIMIT_SECONDS) finishRecording(`The two-minute limit was reached. ${clipReady(current)}`);
          else if (performance.now() - current.wallStarted >= 10 * 60 * 1000) finishRecording('The recording session reached its ten-minute time limit. Your clip is ready to download.');
        }, 250);
        ui.timer.textContent = '00:00';
        say(hasAudio ? 'Recording video with microphone audio in the original camera orientation. Choose Finish clip to see and hear it.' : 'Recording silent video in the original camera orientation. Choose Finish clip to review it.');
      } catch (_) { session = null; say('This browser could not start the recording. Try a lower resolution or use Capture photo.'); }
      sync();
    };
    const pauseRecording = () => {
      if (!session || session.finishing || !session.canPause) return;
      const current = session;
      try {
        if (current.recorder.state === 'recording') {
          const elapsed = current.elapsed + performance.now() - current.started;
          current.recorder.pause();
          current.elapsed = elapsed;
          current.running = false;
          say(current.hasAudio ? 'Recording paused. The live camera and microphone stay on. Resume or finish your clip.' : 'Recording paused. The live camera stays on. Resume or finish your clip.');
        } else if (current.recorder.state === 'paused') {
          current.recorder.resume();
          current.started = performance.now();
          current.running = true;
          say('Recording resumed.');
        }
      } catch (_) { say('Pause is not available right now. Finish this clip and start a new one if needed.'); }
      sync();
    };
    ui.start.addEventListener('click', startCamera);
    ui.stop.addEventListener('click', () => stopCamera(pending ? 'Device request canceled. If the browser still asks for permission, you can dismiss it.' : `${selectedAV() ? 'Camera and microphone' : 'Camera'} stopped. Your captures remain available below until you leave this page.`));
    ui.mode.addEventListener('change', () => {
      if (pending || session) return;
      if (stream) startCamera();
      else { sync(); say(selectedAV() ? 'Camera + microphone selected. Choose Start camera + microphone to allow both devices.' : 'Camera-only mode selected. Start camera will not request your microphone.'); }
    });
    ui.device.addEventListener('change', () => { if (stream) startCamera(); });
    ui.microphone.addEventListener('change', () => { if (stream) startCamera(); });
    ui.resolution.addEventListener('change', () => { if (stream) startCamera(); });
    ui.mirror.addEventListener('change', updateOrientation);
    ui.grid.addEventListener('change', updateOrientation);
    ui.video.addEventListener('loadedmetadata', showDimensions);
    ui.video.addEventListener('resize', showDimensions);
    ui.snapshot.addEventListener('click', snapshot);
    ui.record.addEventListener('click', startRecording);
    ui.pause.addEventListener('click', pauseRecording);
    ui.finish.addEventListener('click', () => finishRecording());
    ui['resume-audio'].addEventListener('click', async () => {
      const target = audioContext;
      const id = requestId;
      if (!target || !stream || !audioAnalyser) return;
      audioResumeError = '';
      try {
        await target.resume();
        if (id !== requestId || audioContext !== target) return;
        syncAudio();
      } catch (_) {
        if (id !== requestId || audioContext !== target) return;
        audioResumeError = 'The microphone meter could not resume. Stop and restart the devices to try again. You can still review audio by recording a clip.';
        syncAudio();
      }
    });
    ui.playback.addEventListener('play', () => {
      if (stream && activeMode === 'audio-video') say('Playing a saved clip. Use headphones or stop the devices to keep speaker playback out of another recording.');
    });
    ui['delete-clip'].addEventListener('click', () => { clearClip(); say('Video deleted from this tab.'); (ui.record.disabled ? ui.start : ui.record).focus(); });
    ui.fullscreen.addEventListener('click', async () => {
      try { await ui.stage.requestFullscreen(); } catch (_) { say('Full screen is unavailable here. The camera preview still works in this page.'); }
    });
    navigator.mediaDevices?.addEventListener?.('devicechange', () => { if (stream) listCameras(requestId); });
    window.addEventListener('pagehide', () => {
      stopCamera('Devices off. Start again when you are ready.', true);
      clearClip();
      photos.forEach((card, url) => { URL.revokeObjectURL(url); card.remove(); });
      photos.clear();
      sync();
    });
    window.addEventListener('beforeunload', () => {
      if (session) session.discard = true;
      stopCamera(null, true);
    });
    if (!supported) say('Camera access needs a secure page in a supported browser. Open this page directly over HTTPS in a current browser.');
    if (!recordingSupported) ui['record-support'].textContent = 'This browser does not support video recording here. Live preview and PNG photos may still work.';
    updateOrientation();
    sync();
  };
  document.querySelectorAll('[data-mm-webcam-test]').forEach(initialize);
})();
